import React, { useMemo, Fragment, ReactElement } from 'react';
import Loader from '../../../../../components/loader/loader.component';
import ActionButton from '../../../../../components/action-button/action-button.component';
import TableRowForm from '../../../../../components/table-row-form/table-row-form.component';
import TableCell from '../../../../../components/table-cell/table-cell.component';
import IconBlockError from '../../../../../components/icon-block-error/icon-block-error.component';
import IconBlockSuccess from '../../../../../components/icon-block-success/icon-block-success.component';
import FormFieldSelect from '../../../../../components/form-field-select/form-field-select.component';
import FormFieldText from '../../../../../components/form-field-text/form-field-text.component';
import FormFieldNumber from '../../../../../components/form-field-number/form-field-number.component';
import FormFieldCheckbox from '../../../../../components/form-field-checkbox/form-field-checkbox.component';
import MenuItemList from '../../../../../components/menu-item-list/menu-item-list.component';
import FormService from '../../../../../services/form-service';
import useShiftState from '../../../../../hooks/use-shift-state';

const SettingsPortfolioRowForm: ReactMemoComponent = React.memo((props: ReactComponentProps): ReactElement => {
  const {
    isActive,
    isSaving,
    isSaveDisabled,
    isInlineSaving,
    inputRefs,
    portfolioData,
    portfolioIndex,
    portfolioMarkerColorList,
    portfolioGroupList,
    onInlineSave,
    onInlineCancel,
  } = props;

  const isShiftPressed: boolean = useShiftState();

  const formKeyPortfolio: string = FormService.getIndexedFormFieldKey(portfolioData.id);
  const formPrefix: string = `portfolios[${formKeyPortfolio}]`;

  const menuItemPortfolioMarkerColorList: ReactElement[] = useMemo((): ReactElement[] => MenuItemList({
    optionItemValueKey: 'name',
    optionList: portfolioMarkerColorList,
  }), [portfolioMarkerColorList]);

  const menuItemPortfolioGroupList: ReactElement[] = useMemo((): ReactElement[] => MenuItemList({
    optionItemLabelKey: 'name',
    optionList: portfolioGroupList,
  }), [portfolioGroupList]);

  const tablePortfolioListFields: TableField[] = useMemo((): TableField[] => [
    {
      id: 1,
      name: 'name',
      component: (field: TableField, fieldIndex: number): ReactElement => (
        <TableCell>
          <FormFieldText
            name={`${formPrefix}.name`}
            inputRefs={inputRefs}
            inputRefIndex={`${portfolioIndex}.${fieldIndex}`}
          />
        </TableCell>
      ),
    },
    {
      id: 2,
      name: 'markerColor',
      component: (field: TableField, fieldIndex: number): ReactElement => (
        <TableCell>
          <FormFieldSelect
            isReadOnly={isShiftPressed}
            name={`${formPrefix}.markerColor`}
            optionsTotal={portfolioMarkerColorList && portfolioMarkerColorList.length}
            inputRefs={inputRefs}
            inputRefIndex={`${portfolioIndex}.${fieldIndex}`}
          >
            {menuItemPortfolioMarkerColorList}
          </FormFieldSelect>
        </TableCell>
      ),
    },
    {
      id: 3,
      name: 'capitalAllocated',
      component: (field: TableField, fieldIndex: number): ReactElement => (
        <TableCell>
          <FormFieldNumber
            name={`${formPrefix}.capitalAllocated`}
            inputRefs={inputRefs}
            inputRefIndex={`${portfolioIndex}.${fieldIndex}`}
          />
        </TableCell>
      ),
    },
    {
      id: 4,
      name: 'groupId',
      component: (field: TableField, fieldIndex: number): ReactElement => (
        <TableCell>
          <FormFieldSelect
            name={`${formPrefix}.groupId`}
            optionsTotal={portfolioGroupList && portfolioGroupList.length}
            inputRefs={inputRefs}
            inputRefIndex={`${portfolioIndex}.${fieldIndex}`}
          >
            {menuItemPortfolioGroupList}
          </FormFieldSelect>
        </TableCell>
      ),
    },
    {
      id: 5,
      name: 'isHidden',
      component: (field: TableField, fieldIndex: number): ReactElement => (
        <TableCell isTextCenter>
          <FormFieldCheckbox
            name={`${formPrefix}.isHidden`}
            inputRefs={inputRefs}
            inputRefIndex={`${portfolioIndex}.${fieldIndex}`}
          />
        </TableCell>
      ),
    },
    {
      id: 6,
      name: 'dealConnectorEmail',
      component: (field: TableField, fieldIndex: number): ReactElement => (
        <TableCell>
          <FormFieldText
            name={`${formPrefix}.dealConnectorEmail`}
            inputRefs={inputRefs}
            inputRefIndex={`${portfolioIndex}.${fieldIndex}`}
          />
        </TableCell>
      ),
    },
    {
      id: 7,
      name: 'actions',
      component: (): ReactElement => (
        <TableCell isActions isTextRight isSticky isStickyRight>
          {
            isSaving && isInlineSaving && (
              <Loader isInline isSmall />
            )
          }
          {
            !isSaveDisabled && !isSaving && (
              <ActionButton
                withSmallPadding
                onClick={(): void => {
                  onInlineSave(portfolioData.id);
                }}
              >
                <IconBlockSuccess />
              </ActionButton>
            )
          }
          {
            !isSaving && (
              <ActionButton
                withSmallPadding
                onClick={(): void => {
                  onInlineCancel(portfolioData.id);
                }}
              >
                <IconBlockError isDisabled={isSaveDisabled} />
              </ActionButton>
            )
          }
        </TableCell>
      ),
    },
  ], [isShiftPressed, isSaving, isSaveDisabled]);

  return (
    <TableRowForm isActive={isActive}>
      {
        tablePortfolioListFields && !!tablePortfolioListFields.length
        && tablePortfolioListFields.map((tableField: TableField, fieldIndex: number): ReactElement | boolean => (
          !!tableField.component && (
            <Fragment key={tableField.id}>
              {tableField.component(tableField, fieldIndex)}
            </Fragment>
          )
        ))
      }
    </TableRowForm>
  );
});

export default SettingsPortfolioRowForm;
